import { TransportChargesReq } from '@gtpl/shared-models/production-management'
import { ProdlogService, ProductionInventoryService } from '@gtpl/shared-services/production'
import { AlertMessages } from '@gtpl/shared-utils/alert-messages'
import { Button, Card, Col, DatePicker, Form, Select, Table } from 'antd'
import { useForm } from 'antd/lib/form/Form'
import React, { useEffect, useMemo, useState } from 'react'
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const ContractorAndDayWiseReport = () => {
    const service = new ProdlogService()
    const filterService = new ProductionInventoryService()
    const [reportData, setReportData] = useState<any[]>([])
    const [contractorData, setContractorData] = useState<any[]>([])
    const [villageData, setVillageData] = useState<any[]>([])
    const [form] =  useForm()

    useEffect(()=>{
        getAllVillages()
        getAllContractors()
    },[])

    const getData = (value)=>{
        const [startDate, endDate] = value?.dateRange?.map(date => date.format('YYYY-MM-DD'));
        const req = new TransportChargesReq(form.getFieldValue("contractorId"), form.getFieldValue("villageId"), startDate, endDate)
        service.getContractorAndDayWiseData(req).then((res)=>{
            if(res.data){
                setReportData(res.data)
            }else{
                setReportData([])
            }
        }).catch((err)=>{
            console.log(err)
        })
    }

      const getAllContractors = () => {
        filterService.getAllContractors().then((res) => {
          if (res.status) {
            setContractorData(res.data);
          } else {
            AlertMessages.getErrorMessage(res.internalMessage);
            setContractorData([]);
          }
        }).catch((err) => {
          AlertMessages.getErrorMessage(err.message);
          setContractorData([]);
        });
      };
    
      const getAllVillages = () => {
        filterService.GetAllVillages().then((res) => {
          if (res.status) {
            setVillageData(res.data);
          } else {
            AlertMessages.getErrorMessage(res.internalMessage);
            setVillageData([]);
          }
        }).catch((err) => {
          AlertMessages.getErrorMessage(err.message);
          setVillageData([]);
        });
      };

    const resetFields=()=>{
        form.resetFields()
        setReportData([])
    }

    const generateColumns = useMemo(() => {
        const baseColumns = [
            { 
                title: 'Contractor', 
                dataIndex: 'contractorName',
                render: (text: string, row: any, index: number) => {
                    const sameContractorRows = reportData.filter(item => item.contractorName === text);
                    const firstOccurrenceIndex = reportData.findIndex(item => item.contractorName === text);

                    return {
                        children: text,
                        props: { rowSpan: index === firstOccurrenceIndex ? sameContractorRows.length : 0 },
                    };
                },
            },
            { title: 'Village', dataIndex: 'villageName' },
        ];
        
        if (!reportData.length) return [...baseColumns, { title: 'Total', dataIndex: 'total' }];

        const dateKeys = Object.keys(reportData[0])
            .filter(key => !['contractorId', 'contractorName', 'villageId', 'villageName', 'total'].includes(key))
            .sort((a, b) => {
                const parseDate = (str: string) => {
                    const [day, month] = str.split(' ');
                    return new Date(`${month} ${day}, 2024`);
                };
                return parseDate(a).getTime() - parseDate(b).getTime();
            });

        const dateColumns = dateKeys.map(key => ({
            title: key,
            dataIndex: key,
            render: (value: string) => value === '0' ? '-' : value
        }));

        return [
            ...baseColumns,
            ...dateColumns,
            { 
                title: 'Total', 
                dataIndex: 'total',
                render: (value: string) => <strong>{value}</strong>
            }
        ];
    }, [reportData]);

    const exportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const formattedFromDate = form.getFieldValue("dateRange")[0].format('DD-MMM-YYYY');
        const formattedToDate = form.getFieldValue("dateRange")[1].format('DD-MMM-YYYY');
        const worksheet = workbook.addWorksheet(`Manpower Details From ${formattedFromDate}`);
    
        const columns = generateColumns;
    
        const lastColumn = String.fromCharCode(65 + columns.length - 1);
    
        const headerTitles = [
            "BMR Industries Pvt. Ltd.",
            "DAMAVARAM, NELLORE.",
            `Manpower Details From ${formattedFromDate} To ${formattedToDate}`
        ];
    
        headerTitles.forEach((title, index) => {
            const rowNumber = index + 1;
            worksheet.mergeCells(`A${rowNumber}:${lastColumn}${rowNumber}`);
            const cell = worksheet.getCell(`A${rowNumber}`);
            cell.value = title;
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.font = { bold: true, size: 14 };
        });
    
        const headerRow = worksheet.addRow(columns.map(col => col.title));
        headerRow.font = { bold: true };
        headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
    
        worksheet.getColumn(1).width = 25;
        worksheet.getColumn(2).width = 20;
    
        let rowIndex = worksheet.rowCount + 1;
        let previousContractor = null;
        let contractorStartRow = null;
        let contractorRowCount = 0;
    
        reportData.forEach((row, index) => {
            const rowData = columns.map(col => row[col.dataIndex] === "0" ? "-" : row[col.dataIndex] || "");
            if (rowData.every(value => value === "" || value === "-")) return;
    
            const newRow = worksheet.addRow(rowData);
            newRow.eachCell(cell => {
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
            });
    
            if (rowData.some(value => value?.toString().toLowerCase().includes("total"))) {
                newRow.eachCell((cell, colNumber) => {
                    if (colNumber >= 1) {
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: 'D9EAD3' }
                        };
                        cell.font = { bold: true };
                    }
                });
            }
    
            if (row.contractorName !== previousContractor) {
                if (contractorStartRow !== null && contractorRowCount > 1) {
                    worksheet.mergeCells(`A${contractorStartRow}:A${contractorStartRow + contractorRowCount - 1}`);
                    worksheet.getCell(`A${contractorStartRow}`).alignment = { horizontal: 'center', vertical: 'middle' };
                }
                previousContractor = row.contractorName;
                contractorStartRow = rowIndex;
                contractorRowCount = 1;
            } else {
                contractorRowCount++;
            }
    
            rowIndex++;
        });
    
        if (contractorStartRow !== null && contractorRowCount > 1) {
            worksheet.mergeCells(`A${contractorStartRow}:A${contractorStartRow + contractorRowCount - 1}`);
            worksheet.getCell(`A${contractorStartRow}`).alignment = { horizontal: 'center', vertical: 'middle' };
        }
    
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, 'Contractor & Day Wise.xlsx');
    };


  return (
    <>
        <Card
            size="small" 
            title={<span style={{ color: 'white' }}>Contractor & Day Wise Report</span>}
            style={{ textAlign: 'center' }} 
            headStyle={{ backgroundColor: '#69c0ff', border: 0 }}
            extra={
                <Button className='panel_button' onClick={() => exportExcel()} disabled={reportData.length === 0}>Get Excel</Button>
              }>
            <Form form={form} layout='inline' onFinish={getData}>
                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 5 }} lg={{ span: 6 }} xl={{ span: 5 }}>
                    <Form.Item name="dateRange" label="Date" rules={[{required: true,message: "select date range"},]}>
                        <DatePicker.RangePicker/>
                    </Form.Item>
                </Col>
                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 5 }} lg={{ span: 6 }} xl={{ span: 5 }}>
                    <Form.Item name="contractorId" label = "Contractor" style={{ width: 200 }}>
                        <Select
                            allowClear
                            showSearch
                            placeholder="Select Contractor"
                            style={{ width: "100%" }}
                            optionFilterProp='children'
                        >
                            {contractorData.map(data => (
                            <Select.Option key={data.contractorId} value={data.contractorId} name = {data.contractor} > {data.contractor}</Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 5 }} lg={{ span: 6 }} xl={{ span: 5 }}>
                    <Form.Item name="villageId" label = "Village" style={{ width: 200 }}>
                        <Select
                            allowClear
                            showSearch
                            placeholder="Select Village"
                            style={{ width: "100%" }}
                            optionFilterProp='children'
                        >
                            {villageData.map(data => (
                            <Select.Option key={data.villageId} value={data.villageId} name = {data.villageName} > {data.villageName}</Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Form.Item>
                    <Button type="primary" htmlType='submit'>Search</Button>
                    <Button style={{ marginLeft: 8 }} onClick={resetFields}>Reset</Button>       
                </Form.Item>
            </Form>
            <br/>
            <Table columns={generateColumns} dataSource={reportData} bordered/>
        </Card>
    </>
  )
}

export default ContractorAndDayWiseReport